import { Invitation, InvitationResponse } from '@features/settings/types/invitation';
import { ApiResponse } from '@shared/types/api';
import { User } from '@shared/types/user';
import axios, { AxiosInstance } from 'axios';
import { authService } from './authService';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000';

class UserService {
  private api: AxiosInstance;
  private static BASE_URL = '/users';
  constructor() {
    this.api = axios.create({
      baseURL: `${API_URL}/auth`,
      withCredentials: true
    });

    // Response interceptor to handle errors
    this.api.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response) {
          // Server responded with an error status
          console.error('Server response error:', error.response.data);
          throw error;
        } else if (error.request) {
          // Request was made but no response received
          console.error('No response received from server');
          throw new Error('Could not connect to server. Please check your connection.');
        } else {
          // Something happened in setting up the request
          console.error('Request setup error:', error.message);
          throw error;
        }
      }
    );
  }

  // Add token to request headers
  setAuthToken(token) {
    if (token) {
      this.api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete this.api.defaults.headers.common['Authorization'];
    }
  }

  // Validate token and get user data
  async validateToken() {
    try {
      const response = await this.api.get<ApiResponse<User>>('/validate');
      return response.data.data;
    } catch (error) {
      console.error('Error validating token:', error);
      throw error;
    }
  }

  // Register user in backend
  async register({ uid, email, name, role, invitationId }: { uid: string, email: string, name: string, role?: string, invitationId?: string | null }) {
    try {
      const response = await this.api.post('/register', {
        uid,
        email,
        displayName: name,
        role,
        invitationId
      });
      return response.data.data;
    } catch (error) {
      console.error('Error registering user:', error);
      throw error;
    }
  }



  /**
   * Validate an invitation
   * @param invitationId Invitation ID
   */
  async validateInvitation(invitationId: string): Promise<Invitation> {
    const response = await this.api.get<InvitationResponse>(
      `/invitation/${invitationId}/validate`
    );
    return response.data.data;
  }

  // Verify email with verification code
  async verifyEmail(code, uid) {
    try {
      const response = await this.api.post('/verify-email', {
        code,
        uid
      });
      return response.data.data;
    } catch (error) {
      console.error('Error verifying email:', error);
      throw error;
    }
  }

  // Resend verification email
  async resendVerificationEmail(uid: string) {
    try {
      const response = await this.api.post('/resend-verification', {
        uid
      });
      return response.data.data;
    } catch (error) {
      console.error('Error resending verification email:', error);
      throw error;
    }
  }

  // Update user profile
  async updateProfile(userData) {
    try {
      const response = await this.api.put('/profile', userData);
      return response.data.data;
    } catch (error) {
      console.error('Error updating profile:', error);
      throw error;
    }
  }

  // Get user by ID
  async getUserById(id: string) {
    try {
      const token = authService.getTokenSync();
      const response = await axios.get(`${API_URL}${UserService.BASE_URL}/${id}`, {
        withCredentials: true,
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      return response.data.data;
    } catch (error) {
      console.error('Error fetching user by ID:', error);
      throw error;
    }
  }

  // Get is user authenticated
  async isUserAuthenticated() {
    try {
      return !!this.api.defaults.headers.common['Authorization'];
    } catch (error) {
      return false;
    }
  }
}

export const userService = new UserService();
